import React, {useEffect, useRef, useState} from 'react';
import {Autocomplete, CircularProgress, TextField} from "@mui/material";
import cl from "./ItemBrowserHeaderSelect.module.css";
import ServerManager from "../API/ServerManager";

const ITEMS_PER_RQ_PAGE = 30;
const TYPING_TIMEOUT = 500; // [ms]

const ItemBrowserHeaderSelect = ({
                                       label,
                                       selectedId,
                                       setSelectedId,
                                       apiName2,
                                       specialOptions = [],
                                       showIds,
                                       itemNameFunc,
                                   }) => {

    const [showIdInInput, setShowIdInInput] = useState(true);

    const rqIdCounter = useRef(0);
    const [status, setStatus] = useState("closed"); // "", closed, idle, mainLoad, mainError, typing
    const [lastTypingTime, setLastTypingTime] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");

    const loadingOption = {id: -1001, name: "loading", isLoading: true}
    const hintOption = {id: -1002, name: "hint", isHint: true}
    const errorOption = {id: -1003, name: "error", isError: true}

    const currentOptionRef = useRef(specialOptions.find(x => x.id === selectedId) ?? {
        id: selectedId,
        name: "-idNotFound-"
    });
    const [mainOptions, setMainOptions] = useState(specialOptions.find(x => x.id === currentOptionRef.current.id) ? specialOptions : [...specialOptions, currentOptionRef.current]);
    const [mainValue, setMainValue] = useState(mainOptions.find(x => x.id === selectedId));

    function filterOptions(options) {
        if (status === "mainLoad" || status === "typing")
            return [loadingOption];

        return options;
    }

    function getOptionLabel(option) {
        return option.name;
    }

    function getOptionKey(option) {
        return option.id;
    }

    function isOptionEqualToValue(option, value) {
        return option.id === value.id;
    }

    function isRegularId(id) {
        return id !== null && id > 0;
    }

    function onFilterEdit(forceUpdate = false) {
        setStatus("typing");
        setLastTypingTime(forceUpdate ? 0 : Date.now());
        setMainOptions(specialOptions.find(x => x.id === currentOptionRef.current.id) ? specialOptions : [...specialOptions, currentOptionRef.current]);
        rqIdCounter.current++;
    }

    async function requestMainItems() {
        rqIdCounter.current++;
        const rqId = rqIdCounter.current;
        //console.log("rqId", rqId);

        setStatus("mainLoad")
        try {
            //await new Promise(resolve => setTimeout(resolve, 1000)); // temp!
            const data = await ServerManager.getSimpleModeFilteredList(apiName2, ITEMS_PER_RQ_PAGE, searchQuery, "name", "asc");
            if (rqIdCounter.current === rqId) {
                const responseList = data[apiName2];
                // TODO? кешировать ответ, если общее кол-во опций мало? (чтобы не переспрашивать одно и то же)

                if (itemNameFunc) {
                    for (const item of responseList)
                        item.name = itemNameFunc(item);
                }

                if (searchQuery) {
                    for (let i = specialOptions.length - 1; i >= 0; i--) {
                        if (specialOptions[i].name.includes(searchQuery))
                            responseList.unshift(specialOptions[i]);
                    }
                } else {
                    if (!specialOptions.find(x => x.id === currentOptionRef.current.id) && !responseList.find(x => x.id === currentOptionRef.current.id)) {
                        responseList.unshift(currentOptionRef.current);
                        responseList.sort((a, b) => a.name.localeCompare(b.name));
                    }
                    for (let i = specialOptions.length - 1; i >= 0; i--)
                        responseList.unshift(specialOptions[i]);
                }

                if (!data.pagination.is_last)
                    responseList.push(hintOption);

                setMainOptions(responseList);
                setStatus("idle");
            } else {
                console.log("rq answer ignored!");
            }
        } catch (error) {
            if (rqIdCounter.current === rqId) {
                //setMainErrorMessage(`Ошибка при загрузке данных: ${error}`);
                setStatus("mainError");

                const responseList = [];

                if (searchQuery) {
                    for (let i = specialOptions.length - 1; i >= 0; i--) {
                        if (specialOptions[i].name.includes(searchQuery))
                            responseList.unshift(specialOptions[i]);
                    }
                } else {
                    if (!specialOptions.find(x => x.id === currentOptionRef.current.id) && !responseList.find(x => x.id === currentOptionRef.current.id)) {
                        responseList.unshift(currentOptionRef.current);
                        //responseList.sort((a, b) => a.name.localeCompare(b.name));
                    }
                    for (let i = specialOptions.length - 1; i >= 0; i--)
                        responseList.unshift(specialOptions[i]);
                }

                responseList.push(errorOption);

                setMainOptions(responseList);
            }
        } finally {
            if (rqIdCounter.current !== rqId) {
                //console.log("ignored! rqId", rqId);
            }
        }
    }

    // запуск/продление временной задержки во время ввода текста
    useEffect(() => {
        async function wait() {
            const leftTime = lastTypingTime + TYPING_TIMEOUT - Date.now();
            if (leftTime > 0)
                await new Promise(resolve => setTimeout(resolve, leftTime));
            if (!ignore)
                requestMainItems();
        }

        if (status !== "typing")
            return;

        let ignore = false;
        wait();
        return () => {
            ignore = true;
        }

    }, [status, lastTypingTime]);

    return <Autocomplete
        fullWidth
        size="small"
        renderInput={params => (
            <TextField
                {...params}
                label={label}
                error={false}
                slotProps={{
                    input: {
                        ...params.InputProps,
                        sx: {
                            ...params.InputProps.sx,
                            backgroundColor: "white",
                            borderRadius: "4px"
                        },
                        endAdornment: showIds && showIdInInput && isRegularId(selectedId) ?
                            (<>{renderIdInfo(selectedId)}{params.InputProps.endAdornment}</>) :
                            (params.InputProps.endAdornment),
                    },
                    inputLabel: {
                        disableAnimation: true,
                        shrink: true,
                    },
                }}
            />
        )}
        options={mainOptions}
        disableClearable
        autoHighlight /* при вводе текста сразу подсвечивается первый из доступных вариантов, что позволяет сразу клавишей Enter завершить выбор */
        blurOnSelect /* после выбора, компонент теряет фокус */
        filterOptions={filterOptions}
        noOptionsText="Варианты не найдены"
        getOptionLabel={getOptionLabel}
        clearText="Очистить"
        openText="Открыть список"
        closeText="Закрыть список"
        value={mainValue}
        getOptionKey={getOptionKey}
        isOptionEqualToValue={isOptionEqualToValue}
        getOptionDisabled={(option) => {
            return option.id < -1000
        }}
        onChange={(event, value) => {
            setMainValue(value);
            currentOptionRef.current = value;
            setSelectedId(value.id);
            /*if (errorMessage)
                setErrorMessage("");*/
        }}
        renderOption={(props2, option) => {
            const {key, ...otherProps} = props2;

            if (option.isHint) {
                return (
                    <li key={key} {...otherProps}
                        style={{
                            display: "block",
                            fontSize: "14px",
                            color: "#844",
                            fontStyle: "italic",
                            opacity: "0.8"
                        }}>
                        <p style={{marginBottom: "-5px"}}>Показаны не все варианты!</p>
                        <p>Уточните поисковый запрос</p>
                    </li>
                );
            }

            if (option.isLoading) {
                return (
                    <li key={key} {...otherProps}
                        style={{display: "flex", gap: "10px", color: "#000", opacity: "0.6"}}>
                        <CircularProgress color="inherit" size={20}/>
                        Поиск вариантов...
                    </li>
                );
            }

            if (option.isError) {
                return (
                    <li key={key} {...otherProps}
                        style={{
                            display: "block",
                            fontSize: "14px",
                            color: "#844",
                            fontStyle: "italic",
                            opacity: "0.8"
                        }}>
                        <p style={{marginBottom: "-5px"}}>Не удалось запросить варианты!</p>
                        <p>Попробуйте повторить поиск</p>
                    </li>
                );
            }

            return (
                <li key={key} {...otherProps}
                    style={{display: "flex", gap: "10px", /*justifyContent: "space-between"*/}}>
                    {option.name}
                    {showIds && isRegularId(option.id) && renderIdInfo(option.id)}
                </li>
            );
        }}
        onInputChange={(e, v, r) => {
            setShowIdInInput(r !== "input");
            if (r === "input") {
                setSearchQuery(v);
                onFilterEdit();
            }
        }}
        onOpen={() => {
            requestMainItems();
        }}
        onClose={(event, reason) => {
            rqIdCounter.current++;
            setMainOptions(specialOptions.find(x => x.id === currentOptionRef.current.id) ? specialOptions : [...specialOptions, currentOptionRef.current]);
            setSearchQuery("");
        }}
    />;
};

function renderIdInfo(id) {
    return <span className={cl.idInfo}>ID {id}</span>
}

export default ItemBrowserHeaderSelect;